import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Alert, Button, TextField, FormControlLabel, Switch,
  Accordion, AccordionSummary, AccordionDetails, Chip, CircularProgress, Snackbar, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import { api } from '../../../api/client.js';
import type { ISlackChannelConfig, ISlackSendResult, SlackChannel, SlackWebhookKind } from '../../../../shared/types.js';
import { colors } from '../../../styles/theme.js';

const KIND_LABEL: Record<SlackWebhookKind, string> = {
  incoming: 'Incoming Webhook',
  trigger: 'Workflow-Trigger',
};

export default function SlackSettingsPage() {
  const [channels, setChannels] = useState<ISlackChannelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  /** Eingaben je Kanal — das Feld ist leer, solange niemand eine neue URL tippt (maskierte URL steht im Platzhalter). */
  const [urlDrafts, setUrlDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, { kind: SlackWebhookKind; json: string }>>({});
  const [testResults, setTestResults] = useState<Record<string, ISlackSendResult>>({});

  const load = async () => {
    const res = await api.get<{ channels: ISlackChannelConfig[] }>('/admin/slack');
    setLoading(false);
    if (res.success) setChannels(res.data.channels);
    else setError(res.error.message);
  };

  useEffect(() => { load(); }, []);

  const patch = async (channel: SlackChannel, body: { enabled?: boolean; webhook_url?: string }) => {
    setBusy(channel);
    const res = await api.patch<{ channels: ISlackChannelConfig[] }>(`/admin/slack/${channel}`, body);
    setBusy(null);
    if (res.success) {
      setChannels(res.data.channels);
      setUrlDrafts((d) => ({ ...d, [channel]: '' }));
      // Eine neue URL kann das Format wechseln — die alte Vorschau waere dann falsch.
      setPreviews((p) => { const n = { ...p }; delete n[channel]; return n; });
      setToast('Gespeichert.');
    } else {
      setError(res.error.message);
    }
  };

  const loadPreview = async (channel: SlackChannel) => {
    const res = await api.get<{ kind: SlackWebhookKind; payload: unknown }>(`/admin/slack/${channel}/preview`);
    if (res.success) {
      setPreviews((p) => ({ ...p, [channel]: { kind: res.data.kind, json: JSON.stringify(res.data.payload, null, 2) } }));
    } else {
      setError(res.error.message);
    }
  };

  const sendTest = async (channel: SlackChannel) => {
    setBusy(channel);
    const res = await api.post<ISlackSendResult>(`/admin/slack/${channel}/test`, {});
    setBusy(null);
    if (res.success) setTestResults((r) => ({ ...r, [channel]: res.data }));
    else setError(res.error.message);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>Slack Config</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
        Zwei getrennte Kanäle mit eigenem Webhook und eigenem Schalter. Ohne hinterlegte
        Webhook-URL wird nie gesendet – auch dann nicht, wenn der Schalter auf aktiv steht.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {channels.map((c) => {
        const draft = urlDrafts[c.channel] ?? '';
        const result = testResults[c.channel];
        const isBusy = busy === c.channel;

        return (
          <Paper key={c.channel} elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{c.label}</Typography>
              <Chip
                size="small"
                label={c.enabled ? 'Aktiv' : 'Inaktiv'}
                sx={{
                  bgcolor: c.enabled ? colors.lime : colors.lightGrey,
                  color: colors.black,
                  fontWeight: 600,
                }}
              />
              {!c.webhook_configured && <Chip size="small" color="warning" label="Keine Webhook-URL" />}
              {c.webhook_kind && <Chip size="small" variant="outlined" label={KIND_LABEL[c.webhook_kind]} />}
              {c.from_env && <Chip size="small" variant="outlined" label="aus .env" />}
            </Box>

            <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>{c.description}</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={c.enabled}
                  disabled={isBusy}
                  onChange={() => patch(c.channel, { enabled: !c.enabled })}
                  color="primary"
                />
              }
              label={c.enabled ? 'Nachrichten gehen raus' : 'Nachrichten werden unterdrückt'}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Incoming-Webhook-URL</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                fullWidth
                sx={{ maxWidth: 520 }}
                disabled={c.from_env || isBusy}
                value={draft}
                onChange={(e) => setUrlDrafts((d) => ({ ...d, [c.channel]: e.target.value }))}
                placeholder={c.webhook_url_masked ?? 'https://hooks.slack.com/triggers/...'}
                helperText={
                  c.from_env
                    ? 'Über die .env gesetzt und hier nicht änderbar.'
                    : c.webhook_configured
                      ? `Hinterlegt: ${c.webhook_url_masked} — zum Ersetzen neue URL eintragen.`
                      : 'Workflow-Trigger (/triggers/…) oder Incoming Webhook (/services/…).'
                }
              />
              <Button
                variant="contained"
                size="small"
                disabled={c.from_env || isBusy || !draft.trim()}
                onClick={() => patch(c.channel, { webhook_url: draft.trim() })}
                sx={{
                  bgcolor: colors.lime, color: colors.black, mt: 0.25,
                  '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey },
                }}
              >
                Speichern
              </Button>
              {c.webhook_configured && !c.from_env && (
                <Button
                  size="small"
                  color="inherit"
                  disabled={isBusy}
                  onClick={() => patch(c.channel, { webhook_url: '' })}
                  sx={{ mt: 0.25, color: colors.black60 }}
                >
                  Entfernen
                </Button>
              )}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SendIcon />}
                disabled={isBusy}
                onClick={() => sendTest(c.channel)}
              >
                Testnachricht senden
              </Button>
              {isBusy && <CircularProgress size={18} />}
            </Box>

            {result && (
              <Alert severity={result.sent ? 'success' : 'warning'} sx={{ mt: 2 }} onClose={() => setTestResults((r) => { const n = { ...r }; delete n[c.channel]; return n; })}>
                {result.sent
                  ? 'Testnachricht wurde an Slack übergeben — schau in den Channel.'
                  : `Nicht gesendet: ${result.reason}`}
              </Alert>
            )}

            <Accordion
              elevation={0}
              disableGutters
              sx={{ mt: 2, border: `1px solid ${colors.silver}`, '&:before': { display: 'none' } }}
              onChange={(_, expanded) => { if (expanded) loadPreview(c.channel); }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Diese Daten würden an Slack übergeben
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" sx={{ color: colors.black60, display: 'block', mb: 1 }}>
                  Beispiel-Payload mit Testdaten — exakt das JSON, das im Ernstfall an die Webhook-URL geht.
                  {previews[c.channel] && ` Format: ${KIND_LABEL[previews[c.channel].kind]}${c.webhook_configured ? ' (aus der hinterlegten URL erkannt)' : ' (Standard, solange keine URL hinterlegt ist)'}.`}
                </Typography>

                {previews[c.channel]?.kind === 'trigger' && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Ein Workflow-Trigger nimmt kein Block Kit, sondern flache Text-Variablen – und
                    zeigt deren Inhalt unformatiert. Lege deshalb am Webhook-Trigger im Workflow
                    Builder diese Variablen an (alle vom Typ Text): <strong>headline</strong>,{' '}
                    <strong>summary</strong>, <strong>details</strong>, <strong>link</strong>,{' '}
                    <strong>link_label</strong>, <strong>environment</strong>, <strong>time</strong>,{' '}
                    <strong>severity</strong>, <strong>hostname</strong>, <strong>count</strong> – dazu
                    weiterhin <strong>text</strong> (alles in einer Variable) und <strong>blocks</strong>.
                    Im Schritt „Nachricht senden" ordnest du sie an und formatierst dort (fett,
                    Zeilenumbrüche); ein interaktiver Button „Link öffnen" bekommt <strong>link</strong> als
                    URL und <strong>link_label</strong> als Beschriftung. Jede Nachricht dieses Kanals
                    liefert alle Variablen, leere als leeren Text.
                  </Alert>
                )}

                <Box
                  component="pre"
                  sx={{
                    m: 0, p: 2, maxHeight: 420, overflow: 'auto',
                    bgcolor: colors.lightGrey, borderRadius: 1,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 12, lineHeight: 1.6,
                  }}
                >
                  {previews[c.channel]?.json ?? 'Wird geladen …'}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>
        );
      })}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast ?? ''} />
    </Box>
  );
}
